import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import '../styles/EventsStyles/WebinarRegistered.css';
import EventsLayout from "../components/EventsSection/EventsLayout";
import { RegistrationReceipt } from "../webinars/types";
import { loadReceipt } from "../webinars/receipts";
import { IS_PROTOTYPE, REGISTRATION_CONTACT } from "../webinars/config";
import { formatDateTime, formatLongDate, formatTimeRange } from "../webinars/format";

type ReceiptWebinar = RegistrationReceipt["webinar"];

// Escape text for an .ics file (commas, semicolons and newlines are special)
const icsText = (value: string) => value.replace(/\\/g, "\\\\").replace(/([,;])/g, "\\$1").replace(/\n/g, "\\n");
const icsDate = (iso: string) => iso.replace(/[-:]/g, "").replace(/\.\d{3}/, "");

// Builds and downloads a calendar file so the attendee can save the session
function downloadCalendarFile(webinar: ReceiptWebinar, reference: string) {
    const lines = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "PRODID:-//Kole Medical//Webinars//EN",
        "BEGIN:VEVENT",
        `UID:${reference}-${webinar.id}@kolemedical`,
        `DTSTAMP:${icsDate(new Date().toISOString())}`,
        `DTSTART:${icsDate(new Date(webinar.startAt).toISOString())}`,
        `DTEND:${icsDate(new Date(webinar.endAt).toISOString())}`,
        `SUMMARY:${icsText(webinar.title)}`,
        `DESCRIPTION:${icsText(`Kole Medical webinar. Your joining link will be emailed to you before the session. Reference: ${reference}.`)}`,
        `LOCATION:${icsText(`Online${webinar.meetingPlatform ? ` via ${webinar.meetingPlatform}` : ""}`)}`,
        "END:VEVENT",
        "END:VCALENDAR",
    ];
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" }));
    link.download = `${webinar.slug}.ics`;
    document.body.appendChild(link);
    link.click();
    link.remove();
}

/** What to say about the confirmation email. "Sent" means handed to the email service, not delivered. */
function confirmationStep(receipt: RegistrationReceipt) {
    switch (receipt.confirmationEmail) {
        case "sent":
            return `We've sent a confirmation to ${receipt.email}. It can take a few minutes to arrive; if you can't find it, check your spam or junk folder.`;
        case "queued":
            return `We'll email a confirmation to ${receipt.email} soon. Your registration is saved: keep your reference number.`;
        case "failed":
            return `We couldn't send your confirmation email just now and will try again automatically. Your registration is saved: keep your reference number.`;
        default:
            return `Prototype: no email is sent. In the live system, a confirmation goes to ${receipt.email}.`;
    }
}

const WebinarRegistered = () => {
    const { slug = "" } = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const reference = searchParams.get("ref") ?? "";
    // Straight after registering the receipt comes with the navigation; after a refresh, from this tab's storage
    const fromNavigation = (location.state as { receipt?: RegistrationReceipt } | null)?.receipt;
    const receipt = fromNavigation?.reference === reference ? fromNavigation : loadReceipt(reference);

    if (!receipt || receipt.webinar.slug !== slug) {
        return (
            <EventsLayout title="Your registration">
                <div className="km-container ev-registered">
                    <h1>Your registration</h1>
                    <p className="km-lead">
                        {reference ? <>Your reference is <strong>{reference}</strong>. </> : null}
                        Registration details are only shown in the browser tab where you registered.{" "}
                        {IS_PROTOTYPE
                            ? "In this prototype, nothing is emailed."
                            : "Your confirmation email has the details of your booking."}
                    </p>
                    <p>
                        Questions about a registration? Email{" "}
                        <a href={`mailto:${REGISTRATION_CONTACT.email}`}>{REGISTRATION_CONTACT.email}</a> or call{" "}
                        {REGISTRATION_CONTACT.phone}.
                    </p>
                    <Link className="km-btn km-btn--primary" to="/events">See upcoming webinars</Link>
                </div>
            </EventsLayout>
        );
    }

    const { webinar } = receipt;
    const when = `${formatLongDate(webinar.startAt)}, ${formatTimeRange(webinar.startAt, webinar.endAt)} SAST`;
    const steps = [
        { title: "Registration saved", when: `${formatDateTime(receipt.registeredAt)} SAST`, tone: "done" },
        { title: "Confirmation email", when: confirmationStep(receipt), tone: receipt.confirmationEmail === "sent" ? "done" : "next" },
        { title: "Joining instructions", when: `We'll email your joining link to ${receipt.email} before the session.`, tone: "next" },
        { title: "Join the webinar", when, tone: "event" },
    ];

    return (
        <EventsLayout title="You are registered">
            <div className="km-container ev-registered">
                <div className="ev-registered-check" aria-hidden="true">
                    <svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                </div>
                <h1>You're registered, {receipt.firstName}.</h1>
                <p className="km-lead">
                    Your registration is saved. Keep your reference number in case you need to contact us about this booking.
                </p>

                <div className="km-card ev-registered-summary">
                    <div className="ev-registered-summary-top">
                        <strong>{webinar.title}</strong>
                        <span className="ev-ref" aria-label={`Reference number ${receipt.reference}`}>{receipt.reference}</span>
                    </div>
                    <dl className="ev-registered-details">
                        <div><dt>When</dt><dd>{when}</dd></div>
                        <div><dt>Format</dt><dd>Online{webinar.meetingPlatform ? ` via ${webinar.meetingPlatform}` : ""}</dd></div>
                        <div><dt>Name</dt><dd>{receipt.firstName} {receipt.lastName}</dd></div>
                        <div><dt>Email</dt><dd>{receipt.email}</dd></div>
                        <div><dt>Phone</dt><dd>{receipt.phone}</dd></div>
                    </dl>
                </div>

                <h2>What happens next</h2>
                <ol className="ev-timeline">
                    {steps.map(step => (
                        <li key={step.title}>
                            <i className={`ev-timeline-dot ev-timeline-dot--${step.tone}`} />
                            <span><strong>{step.title}</strong><small>{step.when}</small></span>
                        </li>
                    ))}
                </ol>

                {IS_PROTOTYPE && (
                    <p className="ev-registered-prototype" role="note">
                        Prototype: this registration is saved only in this browser on this device. Kole Medical has not
                        received it, and no emails are sent.
                    </p>
                )}

                <div className="ev-registered-actions">
                    <button type="button" className="km-btn km-btn--primary" onClick={() => downloadCalendarFile(webinar, receipt.reference)}>
                        Add to calendar
                    </button>
                    <Link className="km-btn km-btn--outline" to="/events">Back to all webinars</Link>
                </div>
            </div>
        </EventsLayout>
    );
};

export default WebinarRegistered;
